const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export class GraphError extends Error {
  constructor(status, body, url) {
    super(`Graph ${status} on ${url}: ${typeof body === 'string' ? body : JSON.stringify(body)}`);
    this.status = status;
    this.body = body;
    this.url = url;
  }
}

export function createGraphClient({ auth, baseUrl = GRAPH_BASE, maxRetries = 5 }) {
  async function request(pathOrUrl, { method = 'GET', headers = {}, body, raw = false, _reauthAttempted = false } = {}) {
    const url = pathOrUrl.startsWith('http') ? pathOrUrl : `${baseUrl}${pathOrUrl}`;
    let attempt = 0;
    while (true) {
      const token = await auth.getToken();
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          ...headers,
        },
        body,
      });

      // 401: cached token was rejected. Force a refresh once and retry.
      if (res.status === 401 && !_reauthAttempted) {
        await auth.getToken({ forceRefresh: true }).catch(() => null);
        return request(pathOrUrl, { method, headers, body, raw, _reauthAttempted: true });
      }

      if (res.status === 429 || (res.status >= 500 && res.status < 600)) {
        if (attempt >= maxRetries) {
          const text = await res.text().catch(() => '');
          throw new GraphError(res.status, text, url);
        }
        const retryAfter = Number(res.headers.get('retry-after'));
        const backoff = Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : Math.min(30_000, 500 * 2 ** attempt);
        await sleep(backoff + Math.random() * 250);
        attempt += 1;
        continue;
      }

      if (!res.ok) {
        let bodyText;
        try {
          bodyText = await res.json();
        } catch {
          bodyText = await res.text().catch(() => '');
        }
        throw new GraphError(res.status, bodyText, url);
      }

      if (raw) return res;
      if (res.status === 204) return null;
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) return res.json();
      return res.text();
    }
  }

  async function* paginate(path, opts = {}) {
    let next = path;
    while (next) {
      const page = await request(next, opts);
      if (Array.isArray(page?.value)) {
        for (const item of page.value) yield item;
      }
      next = page?.['@odata.nextLink'] || null;
    }
  }

  async function download(pathOrUrl, opts = {}) {
    const res = await request(pathOrUrl, { ...opts, raw: true });
    return {
      stream: res.body,
      contentType: res.headers.get('content-type') || 'application/octet-stream',
      contentLength: Number(res.headers.get('content-length')) || null,
      filename: parseFilename(res.headers.get('content-disposition')),
    };
  }

  return { request, paginate, download };
}

function parseFilename(cd) {
  if (!cd) return null;
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (utf) return decodeURIComponent(utf[1].trim().replace(/^"|"$/g, ''));
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1].trim() : null;
}
